import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  AdminOrganizationDetailResponse,
  CreateAdminOrganizationPayload
} from '@utils/types/admin';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export const ADMIN_ORGANIZATIONS_QUERY_KEY = 'adminOrganizations';

/**
 * Hook to create a new on-prem organization
 *
 * POST /api/admin/on-prem/organizations
 */
export const useCreateAdminOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createOrganization = async (
    payload: CreateAdminOrganizationPayload
  ) => {
    const url = `api/admin/on-prem/organizations`;
    const response = await Axios.post<AdminOrganizationDetailResponse>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    AdminOrganizationDetailResponse,
    AxiosError<BaseError>,
    CreateAdminOrganizationPayload
  >(createOrganization, {
    onSuccess: () => {
      toast.success(t('organization-created-successfully'));
      queryClient.invalidateQueries([ADMIN_ORGANIZATIONS_QUERY_KEY]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-create-organization');
      toast.error(String(errorMessage));
    }
  });
};
